import {
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react-dom-interactions"
import { useOnChange } from "@prismicio/editor-support/React"
import { Box, Text } from "@prismicio/editor-ui"
import { Editor, Extension } from "@tiptap/core"
import { ReactRenderer } from "@tiptap/react"
import { Suggestion } from "@tiptap/suggestion"
import {
  forwardRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import Paragraph from "../extensions/Paragraph/Paragraph"
import {
  ALL_NODE_ITEMS,
  filterMenuItems,
  type MenuItem,
} from "../models/MenuItems"

type SlashCommandsOptions = Readonly<{
  nodeTypes: readonly string[]
}>

const NAVIGATION_KEYS = ["ArrowDown", "ArrowUp", "End", "Home"]
const SELECTION_KEYS = ["Enter"]
const HANDLED_KEYS = ["Escape", ...NAVIGATION_KEYS, ...SELECTION_KEYS]

export const SlashCommands = Extension.create<SlashCommandsOptions>({
  name: "slash-commands",

  addOptions() {
    return { nodeTypes: [] }
  },

  addProseMirrorPlugins() {
    const nodeItems = filterMenuItems(ALL_NODE_ITEMS, this.options.nodeTypes)
    if (nodeItems.length > 1) {
      return [
        Suggestion<MenuItem, MenuItem>({
          allowSpaces: true,
          char: "/",
          editor: this.editor,
          startOfLine: true,

          allow: ({ state }) => {
            const $position = state.selection.$from
            const isParagraph =
              $position.parent.type.name === Paragraph.name &&
              $position.depth === 1
            return isParagraph
          },

          command: ({ editor, props, range }) => {
            props.runCommands(editor, { deleteRange: range })
          },

          items: ({ query }) => {
            const searchString = query.toLocaleLowerCase().trim()
            return nodeItems.filter(
              (item) =>
                item.shortText?.toLocaleLowerCase().includes(searchString) ||
                item.text?.toLocaleLowerCase().includes(searchString),
            )
          },

          render: () => {
            let component:
              | ReactRenderer<SlashCommandsViewRef, SlashCommandsViewProps>
              | undefined

            return {
              onStart: (props) => {
                component = new ReactRenderer(SlashCommandsView, {
                  editor: props.editor,
                  props,
                })
                document.body.appendChild(component.element)
              },

              onUpdate: (props) => {
                component?.updateProps(props)
              },

              onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                return Boolean(
                  HANDLED_KEYS.includes(event.key) &&
                    component?.ref?.handleKeyDown(event),
                )
              },

              onExit: () => {
                component?.destroy()
                component?.element.remove()
              },
            }
          },
        }),
      ]
    } else {
      return []
    }
  },
})

interface SlashCommandsViewRef {
  handleKeyDown: (event: KeyboardEvent) => boolean
}

interface SlashCommandsViewProps {
  editor: Editor
  command: (props: MenuItem) => void
  decorationNode: Element | null
  items: MenuItem[]
}

const SlashCommandsView = forwardRef<
  SlashCommandsViewRef,
  SlashCommandsViewProps
>(function SlashCommandsView(props, ref) {
  const { command, decorationNode, editor, items } = props
  const [open, setOpen] = useState(true)

  const { context, floating, reference, x, y } = useFloating({
    middleware: [offset(0), flip(), shift({ limiter: limitShift() })],
    onOpenChange: setOpen,
    open,
    placement: "top-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  })

  useLayoutEffect(() => {
    reference(decorationNode)
  }, [reference, decorationNode])

  useEffect(() => {
    let timeoutId: number | undefined
    const handleFocus = () => {
      // Avoids flickering the floating element when onExit gets called just
      // after onUpdate.
      timeoutId = window.setTimeout(() => {
        setOpen(true)
      })
    }
    const handleBlur = () => {
      setOpen(false)
    }
    editor.on("focus", handleFocus).on("blur", handleBlur)
    return () => {
      editor.off("focus", handleFocus).off("blur", handleBlur)
      clearTimeout(timeoutId)
    }
  }, [editor])

  const [activeIndex, setActiveIndex] = useState(0)
  const listRef = useRef<(HTMLElement | null)[]>([])

  useOnChange(items, () => {
    setActiveIndex(0)
    listRef.current = []
  })

  // TODO: find a way to pass the reference's props to the decoration node.
  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [
      useDismiss(context, { outsidePointerDown: false }),
      useListNavigation(context, {
        activeIndex,
        listRef,
        onNavigate: (index) => {
          setActiveIndex((prevActiveIndex) => index ?? prevActiveIndex)
        },
        openOnArrowKeyDown: false,
        virtual: true,
      }),
      useRole(context, { role: "menu" }),
    ],
  )

  const referenceProps = getReferenceProps()
  const onKeyDown = referenceProps["onKeyDown"] as (e: KeyboardEvent) => void
  useImperativeHandle(
    ref,
    () => ({
      handleKeyDown: (event) => {
        if (open) {
          if (NAVIGATION_KEYS.includes(event.key)) onKeyDown(event)
          if (SELECTION_KEYS.includes(event.key)) {
            const item = items[activeIndex]
            if (item !== undefined) command(item)
          }
          return true
        } else {
          return false
        }
      },
    }),
    [activeIndex, command, items, onKeyDown, open],
  )

  const isPlaced = x !== null && y !== null

  return open ? (
    <Box
      {...getFloatingProps()}
      minWidth={256}
      backgroundColor="grey1"
      border
      borderStyle="solid"
      borderRadius={6}
      flexDirection="column"
      ref={floating}
      top={0}
      left={0}
      position="fixed"
      transform={
        isPlaced
          ? `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`
          : "translate3d(0, -200%, 0)"
      }
      overflow="hidden" // ensure border radius is applied to children
    >
      {items.map((item, index) => {
        // getItemProps returns an unknown record, so the props cannot be destructured before assigning them in the component
        const itemProps = getItemProps({
          onClick: () => {
            command(item)
          },
          onPointerDown: (event) => {
            event.preventDefault()
          },
        })

        return (
          <SlashCommandsViewItem
            {...itemProps}
            key={item.nodeType}
            ref={(element) => {
              listRef.current[index] = element
            }}
            active={index === activeIndex}
            isFirstItem={index === 0}
            isLastItem={index === items.length - 1}
            shortcut={`/${item.shortText ?? ""}`}
          >
            {item.text}
          </SlashCommandsViewItem>
        )
      })}
      {items.length === 0 && (
        <SlashCommandsViewItem
          {...getItemProps({
            onPointerDown: (event) => {
              event.preventDefault()
            },
          })}
          disabled
          ref={(element) => {
            listRef.current[0] = element
          }}
        >
          No results
        </SlashCommandsViewItem>
      )}
    </Box>
  ) : null
})

interface SlashCommandsViewItemProps {
  children?: ReactNode
  disabled?: boolean
  active?: boolean
  isFirstItem?: boolean
  isLastItem?: boolean
  shortcut?: ReactNode
}

const SlashCommandsViewItem = forwardRef<
  HTMLDivElement,
  SlashCommandsViewItemProps
>(function SlashCommandsViewItem(props, ref) {
  const {
    children,
    disabled = false,
    shortcut,
    active = false,
    isFirstItem = false,
    isLastItem = false,
    ...otherProps
  } = props

  return (
    <div
      // getItemProps returns an unknown record, so we can not be explicit with them
      {...otherProps}
      role="button"
      style={{ cursor: disabled ? "default" : "pointer" }}
      ref={ref}
    >
      <Box
        alignItems="center"
        height={32}
        flexDirection="row"
        gap={8}
        padding={8}
        backgroundColor={active ? "grey4" : "inherit"}
        justifyContent="space-between"
        opacity={disabled ? 0.5 : 1}
      >
        <Text color="grey12" component="span">
          {children}
        </Text>
        {shortcut && (
          <Text color="inherit" component="span" variant="small">
            {shortcut}
          </Text>
        )}
      </Box>
    </div>
  )
})
